/**
 * Audit detail — the Lighthouse rich view for one specific audit.
 *
 * The page overview (`../$pageUuid/index.tsx`) shows the analytics
 * trend across many audits; this route zooms into a single run and
 * exposes the data Lighthouse captured for it:
 *
 *   - Metadata header   — id, URL, device/region, time, status
 *   - Scores breakdown  — performance / a11y / best-practices / SEO
 *   - Filmstrip         — render-time screenshots
 *   - Waterfall         — network requests as a Gantt chart
 *   - Opportunities     — savings-ranked fixes (collapsible)
 *   - Diagnostics       — additional info (collapsed by default)
 *
 * Polls while the audit is in flight (the `useAudit` hook handles
 * that). Renders skeletons until the row resolves so the layout
 * doesn't jump.
 */
import { AuditShortcutRow } from '@/components/audits/AuditShortcutRow';
import { Filmstrip } from '@/components/lighthouse/Filmstrip';
import { CategoryAudits, Diagnostics, Opportunities } from '@/components/lighthouse/Opportunities';
import { Waterfall } from '@/components/lighthouse/Waterfall';
import {
  parseCriticalChain,
  parseLabMetrics,
  parseLcpBreakdown,
  parseMainThreadWork,
} from '@/components/lighthouse/audit-insights';
import { resolveLcpElementNode } from '@/components/lighthouse/lcp-element';
import { type LhrJson, useLighthouseReport } from '@/components/lighthouse/use-lhr';
import { ErrorState } from '@/components/primitives/ErrorState';
import { PageActions } from '@/components/primitives/PageActions';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Island } from '@/components/shell/Island';
import { SiteSwitcherCrumb } from '@/components/shell/SiteSwitcherCrumb';
import { Button } from '@/components/ui/button';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/components/ui/menu';
import { useActiveSite } from '@/lib/active-site';
import { apiFetch } from '@/lib/api';
import type { AuditResponse } from '@/lib/api-types';
import { toastFromError, toastSuccess } from '@/lib/errors';
import {
  bandFill,
  bandText,
  clsBand,
  fcpBand,
  formatBytes,
  formatCls,
  formatCount,
  formatMs,
  lcpBand,
  relativeTime,
  scoreBand,
  siBand,
  tbtBand,
  truncate,
  ttfbBand,
} from '@/lib/format';
import { useAudit, usePage, useSite, useSpotAudit } from '@/lib/queries';
import { auditKeys } from '@/lib/queries/query-keys';
import { cn } from '@/lib/utils';
import {
  ArrowClockwise,
  ArrowSquareOut,
  Desktop,
  DeviceMobile,
  DotsThreeVertical,
  Warning,
} from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, type ReactNode, useEffect } from 'react';

export const Route = createFileRoute('/_app/performance/audits/$auditId')({
  component: AuditDetail,
  // Hover-preload: the router fires this loader on link `hover/focus`
  // (see `defaultPreload: 'intent'` in main.tsx). By the time the
  // user clicks an audit row from the pages list, the detail JSON
  // is already in React Query's cache — the click navigation
  // renders instantly with no fetch wait.
  //
  // `include_ids=true` matches what the component's `useAudit` hook
  // calls with — same cache key, same shape, no double fetch when
  // the component then calls `useAudit(auditId, { includeIds: true })`.
  //
  // The Lighthouse report is intentionally NOT preloaded here — it's
  // a multi-MB R2 fetch and most users don't need it for the audit-
  // detail summary view. `useLighthouseReport` fires on click as
  // before for the components that consume it.
  loader: async ({ context: { queryClient }, params }) => {
    // Best-effort preload. We `.catch` so a 401 (Clerk not initialised
    // yet on first-visit URLs) doesn't reject the loader and trip the
    // route's errorComponent — the QueryCache's `routeError` already
    // routes 401s to `/sign-in` independently, and the component's
    // own `useAudit` retries once auth is ready.
    await queryClient
      .ensureQueryData({
        queryKey: auditKeys.detail(params.auditId),
        queryFn: () => apiFetch<AuditResponse>(`/v1/audits/${params.auditId}?include_ids=true`),
        staleTime: 30_000,
      })
      .catch(() => undefined);
  },
});

function AuditDetail(): ReactElement {
  const { auditId } = Route.useParams();

  // `includeIds` gives us the linkage back to site/page even when the
  // user landed here from a deep link — there's no parent route to
  // inherit them from anymore (PR 3 of the analytics-shell redesign
  // collapsed `/sites/*/pages/*/audits/*` to just `/audits/*`).
  const audit = useAudit(auditId, { includeIds: true });
  const siteUuid = audit.data?.ids?.site_uuid ?? null;
  const pageUuid = audit.data?.ids?.page_uuid ?? null;

  // Keep the chrome's active site in sync with the audit on screen.
  // Useful when the user pasted a link to an audit from a site they
  // weren't last on — the sidebar pill should reflect the actual
  // site the audit belongs to.
  const { setActiveSite } = useActiveSite();
  useEffect(() => {
    if (siteUuid) setActiveSite(siteUuid);
  }, [siteUuid, setActiveSite]);

  const site = useSite(siteUuid);
  const page = usePage(pageUuid);
  // Hoisted here so the scores breakdown can fall back to LHR
  // category scores when the audit row's stored scores are null. The
  // sub-views (Filmstrip/Waterfall/Opportunities/Diagnostics) also
  // call `useLighthouseReport` — React Query dedups, so this hook
  // call shares the same in-flight request and cache entry.
  const lhr = useLighthouseReport(auditId);

  // Breadcrumb: Site › Performance › Audits › Page › audit-id. The
  // "Performance" + "Audits" intermediate crumbs keep the hierarchy
  // consistent with the sidebar's Performance → Audits nesting (and
  // the /performance/audits landing, which shows the same shape via
  // SiteIsland) and give the user an explicit climb-back path. All
  // levels are present even before page.data lands so the chrome
  // doesn't restructure when data arrives.
  const breadcrumb = [
    {
      label: site.data?.name ?? siteUuid ?? '',
      render: <SiteSwitcherCrumb />,
    },
    { label: 'Performance', href: '/performance' },
    { label: 'Audits', href: '/performance/audits' },
    ...(pageUuid
      ? [
          {
            label: page.data?.name ?? pageUuid,
            // Lands on the Audits › Pages breakdown with this page
            // preselected — the successor of the removed
            // `/pages/:pageUuid` detail route.
            href: `/performance/audits?page=${pageUuid}`,
          },
        ]
      : []),
    { label: auditId },
  ];

  return (
    <Island
      breadcrumb={breadcrumb}
      actions={
        <PageActions>
          <AuditActionsMenu audit={audit.data} siteAudit={site.data?.audit ?? null} />
        </PageActions>
      }
    >
      {audit.isError ? (
        <ErrorState
          title="Couldn't load this audit"
          error={audit.error}
          retry={() => {
            void audit.refetch();
          }}
        />
      ) : audit.isLoading || !audit.data ? (
        // Loading shape mirrors the populated layout so sections
        // don't visually pop in once data lands. Heights approximate
        // the real sections: shortcut row, metadata header, scores
        // hero, then a handful of analysis sections beneath. We use
        // bordered placeholder frames (not animated skeletons) for
        // the same reason as the page-detail screen — animated
        // shimmers at this density read as "broken" rather than
        // "loading".
        <div className="space-y-6">
          <div
            aria-hidden="true"
            className="h-12 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]"
          />
          <div
            aria-hidden="true"
            className="h-24 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]"
          />
          <div
            aria-hidden="true"
            className="h-32 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]"
          />
          <div
            aria-hidden="true"
            className="h-40 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]"
          />
          <div
            aria-hidden="true"
            className="h-40 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]"
          />
        </div>
      ) : (
        <div className="space-y-6">
          <AuditShortcutRow
            audit={audit.data}
            site={site.data}
            page={page.data}
            siteUuid={siteUuid ?? ''}
            pageUuid={pageUuid ?? ''}
          />
          <MetadataHeader audit={audit.data} />
          <RuntimeErrorBanner audit={audit.data} lhr={lhr.data} />
          <ScoresBreakdown audit={audit.data} lhr={lhr.data} />
          <CoreVitalsSection lhr={lhr.data} />
          <PageWeightSection audit={audit.data} />
          <ThirdPartiesSection audit={audit.data} lhr={lhr.data} />
          <OpportunitiesSummarySection audit={audit.data} />
          <ExecutionSection audit={audit.data} />
          <MainThreadSection lhr={lhr.data} />
          <LcpElementPanel lhr={lhr.data} />
          <LcpBreakdownSection lhr={lhr.data} />
          <Filmstrip auditId={auditId} />
          <CriticalChainSection lhr={lhr.data} />
          <Waterfall auditId={auditId} />
          <Opportunities auditId={auditId} />
          <Diagnostics auditId={auditId} />
          <CategoryAudits
            auditId={auditId}
            categoryId="accessibility"
            title="Accessibility"
            subtitle="Failing accessibility checks from this run."
            icon="accessibility"
          />
          <CategoryAudits
            auditId={auditId}
            categoryId="best-practices"
            title="Best practices"
            subtitle="Failing best-practices checks from this run."
            icon="best-practices"
          />
          <CategoryAudits
            auditId={auditId}
            categoryId="seo"
            title="SEO"
            subtitle="Failing SEO checks from this run."
            icon="seo"
          />
        </div>
      )}
    </Island>
  );
}

// ─── Metadata header ────────────────────────────────────────────────
//
// Quiet card with the audit's facts: id, URL, when, device, region,
// status. Lives at the top so the rest of the page reads as
// "everything below describes THIS audit."

function MetadataHeader({ audit }: { audit: AuditResponse }): ReactElement {
  const completed = audit.completed_at ?? audit.created_at;
  const DeviceIcon = audit.device === 'mobile' ? DeviceMobile : Desktop;
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-[var(--color-ink-soft)]">{audit.id}</span>
            <StatusBadge status={audit.status} />
          </div>
          <div className="mt-1 truncate text-sm text-[var(--color-mute)]">
            {truncate(audit.url, 96)}
          </div>
        </div>
        <dl className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-[var(--color-mute)]">
          <Field label="Device">
            <span className="inline-flex items-center gap-1 text-[var(--color-ink-soft)]">
              <DeviceIcon aria-hidden="true" size={12} />
              {audit.device ?? '—'}
            </span>
          </Field>
          {audit.region && (
            <Field label="Region">
              <span className="text-[var(--color-ink-soft)]">{audit.region}</span>
            </Field>
          )}
          <Field label="When">
            <span className="text-[var(--color-ink-soft)]">{relativeTime(completed)}</span>
          </Field>
          {audit.trigger && (
            <Field label="Trigger">
              <span className="text-[var(--color-ink-soft)]">{triggerLabel(audit.trigger)}</span>
            </Field>
          )}
          {audit.lighthouse_version && (
            // Pure metadata — useful for explaining score drift after
            // LH version bumps. Display only.
            <Field label="Lighthouse">
              <span className="font-mono text-[var(--color-ink-soft)]">
                {audit.lighthouse_version}
              </span>
            </Field>
          )}
        </dl>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactElement }): ReactElement {
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.06em]">{label}</dt>
      <dd className="text-xs tabular-nums">{children}</dd>
    </div>
  );
}

// ─── Re-run combination card ────────────────────────────────────────
//
// The page's whole action set, folded into the header's overflow menu.
//
// These used to be two header buttons plus a body card. "Back to page"
// duplicated the breadcrumb, and the re-run card spent a full-width row
// explaining an action most visits never take — so the header is now
// just the ⋮, matching the page-detail screen.
//
// Re-run spot-audits the audit's URL with the audit's own device +
// region, landing as `trigger='web'` (dashboard JWT) so it's comparable
// with other dashboard-initiated runs. It only makes sense when:
//   1. The audit row carries `ids` (so we know the tuple).
//   2. That tuple is still in the site's current audit grid
//      (regions × devices). If the user edited coverage after this
//      audit ran, the tuple is no longer comparable to current data.
// Failing (2) the item stays visible but disabled, and says why in its
// own label — a disabled row with a tooltip would explain nothing on a
// phone, where there's no hover.

type SiteAuditConfig = NonNullable<ReturnType<typeof useSite>['data']>['audit'];

function AuditActionsMenu({
  audit,
  siteAudit,
}: {
  /** Undefined until the audit row lands — items disable themselves. */
  audit: AuditResponse | undefined;
  siteAudit: SiteAuditConfig | null;
}): ReactElement {
  const run = useSpotAudit();

  const ids = audit?.ids;
  const rerunnable = !!ids && !!audit?.url;
  const inGrid =
    !!ids &&
    !!siteAudit &&
    siteAudit.regions.some((r) => r.uuid === ids.region_uuid) &&
    siteAudit.devices.some((d) => d.uuid === ids.device_profile_uuid);

  async function onRerun(): Promise<void> {
    if (!ids || !audit?.url) return;
    try {
      // `spot_audit` takes the URL + the exact device/region the
      // original audit ran with. Server resolves the device-profile
      // uuid → preset_key under the hood, so passing the uuid via
      // `device_profile_uuid` is sufficient. We don't need to look
      // up site/page — the URL plus the tuple is enough to pin the
      // re-run to the same data point.
      await run.mutateAsync({
        body: {
          url: audit.url,
          device_profile_id: ids.device_profile_uuid,
          region: ids.region_uuid,
        },
      });
      toastSuccess('Audit re-run queued');
    } catch (err) {
      toastFromError(err);
    }
  }

  return (
    <Menu>
      <MenuTrigger render={<Button size="icon" variant="ghost" aria-label="More actions" />}>
        <DotsThreeVertical aria-hidden="true" />
      </MenuTrigger>
      <MenuPopup align="end">
        {audit?.url && (
          <MenuItem
            // biome-ignore lint/a11y/useAnchorContent: menu item children pass through to <a>
            render={<a href={audit.url} target="_blank" rel="noreferrer noopener" />}
          >
            <ArrowSquareOut aria-hidden="true" />
            Visit URL
          </MenuItem>
        )}
        {rerunnable && (
          <MenuItem
            disabled={!inGrid || run.isPending}
            {...(inGrid
              ? {}
              : {
                  title:
                    "This combination isn't in the current audit configuration. Adjust regions or devices on the site settings page to re-run it.",
                })}
            onClick={() => {
              void onRerun();
            }}
          >
            <ArrowClockwise aria-hidden="true" />
            {!inGrid
              ? 'Re-run unavailable — coverage changed'
              : run.isPending
                ? 'Queueing re-run…'
                : 'Re-run this combination'}
          </MenuItem>
        )}
      </MenuPopup>
    </Menu>
  );
}

// ─── Runtime-error banner ───────────────────────────────────────────
//
// Lighthouse populates `lhr.runtimeError` when it couldn't navigate
// to the page (DNS, TLS, HTTP error, etc.). When this happens, every
// performance audit ends up `scoreDisplayMode: "error"` with zero
// items — Filmstrip, Waterfall, Opportunities all silent-null, and
// the audit looks confusingly empty alongside a green "success"
// status badge from the runner (which is reporting that the runner
// itself ran cleanly, not that Lighthouse got useful data).
//
// Surface the LH error here so the user instantly sees *why* the
// rest of the page is bare. Silent when there's no runtime error.

/**
 * Two sources of error info, in priority order:
 *
 *   1. `audit.error.explanation` — resolved API-side from a curated
 *      lookup (packages/shared/src/lh-errors.ts). Always present for
 *      errored audits whose `error_message` matches a known LH code.
 *      This is the only source for errored audits before the LHR has
 *      loaded (or when the LHR isn't available at all, e.g. errored
 *      runs that didn't propagate report_r2_key — see spawned task).
 *
 *   2. `lhr.runtimeError` — used as fallback when (1) is absent (older
 *      audits, unknown codes). Generic Lighthouse message; not as
 *      actionable.
 */
function RuntimeErrorBanner({
  audit,
  lhr,
}: {
  audit: AuditResponse;
  lhr: LhrJson | null | undefined;
}): ReactElement | null {
  const explanation = audit.error?.explanation;
  const lhRuntimeErr = lhr?.runtimeError as { code?: string; message?: string } | undefined;
  const lhCode = lhRuntimeErr?.code && lhRuntimeErr.code !== 'NO_ERROR' ? lhRuntimeErr.code : null;

  // Render-gate: explanation OR a non-NO_ERROR LHR code OR an audit
  // row in error/timeout state. The audit row check catches errored
  // audits that don't yet have an `explanation` (unknown LH code) so
  // the user still sees *something* useful instead of silence.
  const isErrored = audit.status === 'error' || audit.status === 'timeout';
  if (!explanation && !lhCode && !isErrored) return null;

  const title = explanation?.title ?? "Lighthouse couldn't capture this run";
  const detailMessage = audit.error?.message ?? lhRuntimeErr?.message ?? null;
  const codeChip = explanation?.code ?? lhCode ?? audit.error?.code ?? null;

  return (
    <section className="flex items-start gap-3 rounded-xl border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/5 p-4">
      <Warning
        aria-hidden="true"
        weight="light"
        size={20}
        className="mt-0.5 shrink-0 text-[var(--color-danger)]"
      />
      <div className="min-w-0 space-y-2">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
          {/* Always show the raw message — sometimes carries useful
              specifics (failing URL, HTTP status) the curated copy
              doesn't have. Falls back to silence when neither source
              has a message. */}
          {detailMessage && <p className="text-xs text-[var(--color-ink-soft)]">{detailMessage}</p>}
        </div>
        {explanation && explanation.causes.length > 0 && (
          <div className="space-y-1.5 border-t border-[var(--color-danger)]/15 pt-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-ink-soft)]">
              Common causes
            </p>
            <ul className="space-y-1 text-xs text-[var(--color-ink-soft)]">
              {explanation.causes.map((c) => (
                <li key={c} className="flex gap-1.5">
                  <span aria-hidden="true" className="text-[var(--color-mute)]">
                    •
                  </span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {codeChip && <p className="font-mono text-[10px] text-[var(--color-mute)]">{codeChip}</p>}
      </div>
    </section>
  );
}

// ─── Scores breakdown ───────────────────────────────────────────────
//
// 4 Lighthouse category scores rendered as dial chips. The audit's
// exact scores; no aggregation. Bands inherit from the existing tone
// helpers (90+ green, 50–89 amber, <50 red).

function ScoresBreakdown({
  audit,
  lhr,
}: {
  audit: AuditResponse;
  lhr: LhrJson | null | undefined;
}): ReactElement {
  // Two sources of truth: the audit row's stored `scores` (canonical
  // when the metrics-extraction pipeline succeeded) and the raw LHR
  // `categories.<id>.score` (0–1 float). Some older snapshots
  // completed before task #61 landed and have null `scores` on the
  // row — fall back to the LHR so the dials still populate.
  const lhrCategoryScore = (id: string): number | null => {
    const cat = lhr?.categories?.[id];
    if (!cat || typeof cat.score !== 'number') return null;
    return Math.round(cat.score * 100);
  };
  const s = audit.scores ?? null;
  const items: Array<{ label: string; value: number | null }> = [
    { label: 'Performance', value: s?.performance ?? lhrCategoryScore('performance') },
    { label: 'Accessibility', value: s?.accessibility ?? lhrCategoryScore('accessibility') },
    { label: 'Best practices', value: s?.best_practices ?? lhrCategoryScore('best-practices') },
    { label: 'SEO', value: s?.seo ?? lhrCategoryScore('seo') },
  ];
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <header className="mb-3">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">Lighthouse scores</h3>
        <p className="text-xs text-[var(--color-mute)]">
          Category-level scores from this audit run.
        </p>
      </header>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((it) => (
          <ScoreDial key={it.label} label={it.label} value={it.value} />
        ))}
      </div>
    </section>
  );
}

/**
 * Map the trigger channel enum to a user-friendly label. Centralised
 * so the audit-detail field, list rows, and any future SourceChip
 * pull from the same dictionary. Keep in sync with
 * `packages/shared/src/audit-group-event-payload.ts` and
 * `snapshot_group_trigger`.
 */
function triggerLabel(t: NonNullable<AuditResponse['trigger']>): string {
  switch (t) {
    case 'scheduled':
      return 'Scheduled';
    case 'web':
      return 'Dashboard';
    case 'agent':
      return 'API';
    case 'cli':
      return 'CLI';
    case 'variant':
      return 'Variant';
  }
}

function ScoreDial({ label, value }: { label: string; value: number | null }): ReactElement {
  const band = scoreBand(value);
  const tone = bandText(band);
  const stroke = bandFill(band);
  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value)) / 100;
  return (
    <div className="flex items-center gap-3">
      <svg width={56} height={56} viewBox="0 0 56 56" aria-hidden="true">
        <circle cx={28} cy={28} r={radius} fill="none" stroke="var(--color-line)" strokeWidth={4} />
        <circle
          cx={28}
          cy={28}
          r={radius}
          fill="none"
          stroke={stroke}
          strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray={`${circumference * pct} ${circumference}`}
          transform="rotate(-90 28 28)"
        />
      </svg>
      <div>
        <div className={cn('text-xl font-semibold tabular-nums', tone)}>
          {value == null ? '—' : Math.round(value)}
        </div>
        <div className="text-xs uppercase tracking-[0.06em] text-[var(--color-mute)]">{label}</div>
      </div>
    </div>
  );
}

// ─── Compact fact tile ───────────────────────────────────────────────
//
// Single-audit facts need a tile that's denser than `MetricTile` — no
// trend histogram (a single run has nothing to plot), just label +
// value. Local to this file because no other surface renders a
// single-audit fact grid today; if a second consumer appears we can
// promote it to `components/primitives/`.

function MetricFact({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  /** Optional secondary line under the value — used for unit hints or context. */
  hint?: string;
}): ReactElement {
  return (
    <div className="rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)] p-3">
      <div className="truncate text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums text-[var(--color-ink)]">{value}</div>
      {hint && <div className="mt-0.5 text-[10px] text-[var(--color-mute)]">{hint}</div>}
    </div>
  );
}

/** Section wrapper — labeled cluster with consistent chrome. */
function MetricSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <header className="mb-3">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
        {subtitle && <p className="text-xs text-[var(--color-mute)]">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

// ─── Page weight section ─────────────────────────────────────────────
//
// Total + per-bucket bytes from `snapshots.summary`. The stacked bar
// at the top is a quick visual answer to "where's the weight going?";
// the tile row below shows the absolute numbers. The bar derives "Other"
// from `total - sum(buckets)` so the visualisation always sums to the
// total even when LH classified some requests outside the named
// mimeType buckets.

const PAGE_WEIGHT_BUCKETS = [
  { key: 'html_bytes', label: 'HTML', color: 'var(--color-accent-brand)' },
  { key: 'js_bytes', label: 'JS', color: 'var(--color-warning)' },
  { key: 'css_bytes', label: 'CSS', color: 'var(--color-success)' },
  { key: 'image_bytes', label: 'Images', color: 'var(--color-info, #3b82f6)' },
  { key: 'font_bytes', label: 'Fonts', color: 'var(--color-danger)' },
] as const;

function PageWeightSection({ audit }: { audit: AuditResponse }): ReactElement | null {
  const m = audit.metrics;
  if (!m) return null;
  const total = m.total_bytes ?? null;
  const buckets = PAGE_WEIGHT_BUCKETS.map((b) => ({
    ...b,
    value: (m[b.key] ?? null) as number | null,
  }));
  // Hide the section entirely on pre-extension snapshots — total + all
  // bucket fields are null. Once any one resolves we render the rest as
  // "—" rather than ducking the whole section.
  if (total == null && buckets.every((b) => b.value == null)) return null;

  // Bar segments — collapse nulls to 0 so we don't crash the divide.
  const bucketSum = buckets.reduce((acc, b) => acc + (b.value ?? 0), 0);
  const other = total != null && total > bucketSum ? total - bucketSum : 0;
  const denominator = total ?? bucketSum + other;
  const segments =
    denominator > 0
      ? [
          ...buckets
            .filter((b) => (b.value ?? 0) > 0)
            .map((b) => ({ label: b.label, value: b.value ?? 0, color: b.color })),
          ...(other > 0 ? [{ label: 'Other', value: other, color: 'var(--color-mute)' }] : []),
        ]
      : [];

  return (
    <MetricSection
      title="Page weight"
      subtitle="Bytes transferred over the wire, grouped by resource type."
    >
      {segments.length > 0 && (
        <div className="mb-3">
          <StackedBar segments={segments} total={denominator} ariaLabel="Page weight breakdown" />
        </div>
      )}
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))]">
        <MetricFact label="Total" value={formatBytes(total)} />
        {buckets.map((b) => (
          <MetricFact key={b.key} label={b.label} value={formatBytes(b.value)} />
        ))}
      </div>
    </MetricSection>
  );
}

/**
 * Inline stacked-bar — pure SVG, no chart lib. Segments render
 * left-to-right proportional to their share of `total`. Each segment
 * carries a `title` for hover-text fallbacks. `format` controls how the
 * legend/tooltip values read (bytes by default; pass `formatMs` for the
 * timing breakdowns).
 */
function StackedBar({
  segments,
  total,
  format = formatBytes,
  ariaLabel = 'Breakdown',
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  total: number;
  format?: (value: number) => string;
  ariaLabel?: string;
}): ReactElement {
  let x = 0;
  return (
    <div className="space-y-2">
      <svg
        viewBox="0 0 100 6"
        preserveAspectRatio="none"
        className="block h-2.5 w-full overflow-hidden rounded-full"
        aria-label={ariaLabel}
      >
        <title>{ariaLabel}</title>
        {segments.map((s) => {
          const w = (s.value / total) * 100;
          const rect = (
            <rect key={s.label} x={x} y={0} width={w} height={6} fill={s.color}>
              <title>{`${s.label}: ${format(s.value)}`}</title>
            </rect>
          );
          x += w;
          return rect;
        })}
      </svg>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[var(--color-mute)]">
        {segments.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1">
            <span
              aria-hidden="true"
              className="inline-block size-2 rounded-sm"
              style={{ background: s.color }}
            />
            {s.label}
            <span className="tabular-nums">({format(s.value)})</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Third parties section ───────────────────────────────────────────
//
// Aggregate facts come from `snapshots.summary` (cheap); the per-entity
// top-N table reads `third-party-summary` items from the raw LHR. The
// LHR is already fetched higher up for the scores fallback / runtime-
// error banner, so this doesn't add a second request.

interface ThirdPartySummaryItem {
  entity?: string | { text?: string };
  transferSize?: number;
  blockingTime?: number;
  mainThreadTime?: number;
}

function ThirdPartiesSection({
  audit,
  lhr,
}: {
  audit: AuditResponse;
  lhr: LhrJson | null | undefined;
}): ReactElement | null {
  const m = audit.metrics;
  if (!m) return null;
  const bytes = m.third_party_bytes ?? null;
  const blocking = m.third_party_blocking_ms ?? null;
  const count = m.third_party_entity_count ?? null;
  if (bytes == null && blocking == null && count == null) return null;

  const items: ThirdPartySummaryItem[] =
    (lhr?.audits?.['third-party-summary']?.details?.items as ThirdPartySummaryItem[]) ?? [];
  const topEntities = [...items]
    .map((it) => ({
      name: typeof it.entity === 'string' ? it.entity : (it.entity?.text ?? '—'),
      transferSize: it.transferSize ?? 0,
      blockingTime: it.blockingTime ?? 0,
    }))
    .sort((a, b) => b.transferSize - a.transferSize)
    .slice(0, 5);

  return (
    <MetricSection
      title="Third parties"
      subtitle="Resources loaded from external entities (analytics, ads, fonts, embeds)."
    >
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))]">
        <MetricFact label="Total bytes" value={formatBytes(bytes)} />
        <MetricFact label="Blocking time" value={formatMs(blocking)} />
        <MetricFact label="Entities" value={formatCount(count)} />
      </div>
      {topEntities.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-md border border-[var(--color-line-soft)]">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--color-line-soft)] text-left text-[var(--color-mute)]">
                <th className="px-3 py-1.5 font-semibold uppercase tracking-[0.04em]">Entity</th>
                <th className="px-3 py-1.5 text-right font-semibold uppercase tracking-[0.04em]">
                  Transfer
                </th>
                <th className="px-3 py-1.5 text-right font-semibold uppercase tracking-[0.04em]">
                  Blocking
                </th>
              </tr>
            </thead>
            <tbody>
              {topEntities.map((e) => (
                <tr
                  key={e.name}
                  className="border-b border-[var(--color-line-soft)]/70 last:border-0"
                >
                  <td className="truncate px-3 py-1.5 text-[var(--color-ink-soft)]">{e.name}</td>
                  <td className="px-3 py-1.5 text-right text-[var(--color-ink-soft)] tabular-nums">
                    {formatBytes(e.transferSize)}
                  </td>
                  <td className="px-3 py-1.5 text-right text-[var(--color-ink-soft)] tabular-nums">
                    {formatMs(e.blockingTime)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length > topEntities.length && (
            <div className="px-3 py-1.5 text-[10px] text-[var(--color-mute)]">
              + {items.length - topEntities.length} more entities
            </div>
          )}
        </div>
      )}
    </MetricSection>
  );
}

// ─── Opportunities summary section ───────────────────────────────────
//
// Quick-glance numbers for the three opportunities we surface on the
// summary. The detailed per-opportunity list (with collapsible rows +
// affected resources) lives in <Opportunities /> below — this section
// is the at-a-glance number, not a replacement.

function OpportunitiesSummarySection({ audit }: { audit: AuditResponse }): ReactElement | null {
  const m = audit.metrics;
  if (!m) return null;
  const unusedJs = m.unused_js_bytes ?? null;
  const unusedCss = m.unused_css_bytes ?? null;
  const rbBytes = m.render_blocking_bytes ?? null;
  const rbCount = m.render_blocking_count ?? null;
  if (unusedJs == null && unusedCss == null && rbBytes == null && rbCount == null) return null;

  return (
    <MetricSection
      title="Opportunities"
      subtitle="Potential savings — see the detailed list below for affected resources."
    >
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))]">
        <MetricFact label="Unused JS" value={formatBytes(unusedJs)} />
        <MetricFact label="Unused CSS" value={formatBytes(unusedCss)} />
        <MetricFact
          label="Render-blocking"
          value={formatBytes(rbBytes)}
          hint={rbCount != null ? `${rbCount} resource${rbCount === 1 ? '' : 's'}` : undefined}
        />
      </div>
    </MetricSection>
  );
}

// ─── Execution section ───────────────────────────────────────────────

function ExecutionSection({ audit }: { audit: AuditResponse }): ReactElement | null {
  const m = audit.metrics;
  if (!m) return null;
  const bootup = m.bootup_time_ms ?? null;
  const mainthread = m.mainthread_total_ms ?? null;
  const requests = m.request_count ?? null;
  const domNodes = m.dom_node_count ?? null;
  const domDepth = m.dom_max_depth ?? null;
  if (
    bootup == null &&
    mainthread == null &&
    requests == null &&
    domNodes == null &&
    domDepth == null
  ) {
    return null;
  }

  return (
    <MetricSection
      title="Execution & DOM"
      subtitle="How much the browser had to chew on this page."
    >
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))]">
        <MetricFact label="JS bootup" value={formatMs(bootup)} />
        <MetricFact label="Main thread" value={formatMs(mainthread)} />
        <MetricFact label="Requests" value={formatCount(requests)} />
        <MetricFact label="DOM nodes" value={formatCount(domNodes)} />
        <MetricFact label="DOM depth" value={formatCount(domDepth)} />
      </div>
    </MetricSection>
  );
}

// ─── LCP element panel ───────────────────────────────────────────────
//
// "What IS your LCP?" — Lighthouse identifies the DOM node that
// triggered LCP (`largest-contentful-paint-element` on LH ≤12, the
// LCP insight audits on LH 13 — see lcp-element.ts). We surface its
// selector + text snippet so the user can act on it without
// round-tripping through the raw LHR. Pulled from LHR (not summary
// JSONB) because the selector string is too page-specific to be useful
// in aggregate analytics.

function LcpElementPanel({ lhr }: { lhr: LhrJson | null | undefined }): ReactElement | null {
  const node = resolveLcpElementNode(lhr);
  if (!node || (!node.selector && !node.snippet)) return null;

  return (
    <MetricSection
      title="Largest Contentful Paint element"
      subtitle="The DOM node Lighthouse identified as the LCP candidate."
    >
      <div className="space-y-2">
        {node.nodeLabel && (
          <div className="text-xs text-[var(--color-ink-soft)]">{node.nodeLabel}</div>
        )}
        {node.selector && (
          <pre className="overflow-x-auto rounded-md border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)] px-3 py-2 text-[11px] text-[var(--color-ink-soft)]">
            <code>{node.selector}</code>
          </pre>
        )}
        {node.snippet && (
          <pre className="overflow-x-auto rounded-md border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)] px-3 py-2 text-[11px] text-[var(--color-ink-soft)]">
            <code>{node.snippet}</code>
          </pre>
        )}
      </div>
    </MetricSection>
  );
}

// ─── Web Vitals strip ────────────────────────────────────────────────
//
// The headline lab metrics behind the performance score — the numbers
// the ScoresBreakdown dial (a single 0–100) doesn't show. Simulated
// (lantern) values from the `metrics` audit + TTFB, each banded
// good/needs-improvement/poor.

function CoreVitalsSection({ lhr }: { lhr: LhrJson | null | undefined }): ReactElement | null {
  const m = parseLabMetrics(lhr);
  if (!m) return null;
  const vitals = [
    { label: 'LCP', value: formatMs(m.lcp), band: lcpBand(m.lcp) },
    { label: 'FCP', value: formatMs(m.fcp), band: fcpBand(m.fcp) },
    { label: 'CLS', value: formatCls(m.cls), band: clsBand(m.cls) },
    { label: 'TBT', value: formatMs(m.tbt), band: tbtBand(m.tbt) },
    { label: 'Speed Index', value: formatMs(m.si), band: siBand(m.si) },
    { label: 'TTFB', value: formatMs(m.ttfb), band: ttfbBand(m.ttfb) },
  ];
  return (
    <MetricSection
      title="Web Vitals"
      subtitle="Lab metrics from this run — the numbers behind the performance score."
    >
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))]">
        {vitals.map((v) => (
          <div
            key={v.label}
            className="rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)] p-3"
          >
            <div className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block size-2 shrink-0 rounded-full"
                style={{ background: bandFill(v.band) }}
              />
              <span className="truncate text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
                {v.label}
              </span>
            </div>
            <div className={cn('mt-1 text-lg font-semibold tabular-nums', bandText(v.band))}>
              {v.value}
            </div>
          </div>
        ))}
      </div>
    </MetricSection>
  );
}

// ─── Main-thread work breakdown ──────────────────────────────────────
//
// `mainthread-work-breakdown` — how the CPU time on the main thread
// split across script eval / parse / style & layout / etc. The
// ExecutionSection shows the *total*; this shows where it went.

const THREAD_COLORS = [
  'var(--color-warning)',
  'var(--color-accent-brand)',
  'var(--color-success)',
  'var(--color-danger)',
  'var(--color-info, #3b82f6)',
  'var(--color-mute)',
];

function MainThreadSection({ lhr }: { lhr: LhrJson | null | undefined }): ReactElement | null {
  const work = parseMainThreadWork(lhr);
  if (!work) return null;
  const segments = work.groups.map((g, i) => ({
    label: g.label,
    value: g.ms,
    color: THREAD_COLORS[i % THREAD_COLORS.length]!,
  }));
  return (
    <MetricSection
      title="Main-thread work"
      subtitle="How the CPU time on the main thread was spent."
    >
      <div className="mb-3">
        <StackedBar
          segments={segments}
          total={work.totalMs}
          format={formatMs}
          ariaLabel="Main-thread work breakdown"
        />
      </div>
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))]">
        <MetricFact label="Total" value={formatMs(work.totalMs)} />
        {work.groups.map((g) => (
          <MetricFact key={g.group} label={g.label} value={formatMs(g.ms)} />
        ))}
      </div>
    </MetricSection>
  );
}

// ─── LCP breakdown ───────────────────────────────────────────────────
//
// `lcp-breakdown-insight` — LCP split into its phases (TTFB → resource
// load delay → load time → element render delay). Pairs with the LCP
// element panel above: that says *what* the LCP is, this says *why it's
// slow*.

const LCP_PHASE_COLORS = [
  'var(--color-accent-brand)',
  'var(--color-warning)',
  'var(--color-success)',
  'var(--color-danger)',
];

function LcpBreakdownSection({ lhr }: { lhr: LhrJson | null | undefined }): ReactElement | null {
  const b = parseLcpBreakdown(lhr);
  if (!b) return null;
  const segments = b.phases.map((p, i) => ({
    label: p.label,
    value: p.ms,
    color: LCP_PHASE_COLORS[i % LCP_PHASE_COLORS.length]!,
  }));
  return (
    <MetricSection title="LCP breakdown" subtitle="Where the time until the largest paint went.">
      <div className="mb-3">
        <StackedBar
          segments={segments}
          total={b.totalMs}
          format={formatMs}
          ariaLabel="LCP breakdown"
        />
      </div>
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))]">
        <MetricFact label="Total (LCP)" value={formatMs(b.totalMs)} />
        {b.phases.map((p) => (
          <MetricFact key={p.subpart} label={p.label} value={formatMs(p.ms)} />
        ))}
      </div>
    </MetricSection>
  );
}

// ─── Critical request chain ──────────────────────────────────────────
//
// `network-dependency-tree-insight` — the dependency chains the browser
// followed to render. The waterfall shows *when* requests happen; this
// shows *what blocks what*. The longest (critical) path is highlighted.

/** Last path segment for a chain node, or the hostname for the root doc. */
function chainLabel(url: string): string {
  try {
    const u = new URL(url);
    const seg = u.pathname.split('/').filter(Boolean).pop();
    return seg ?? u.hostname;
  } catch {
    return url;
  }
}

function CriticalChainSection({ lhr }: { lhr: LhrJson | null | undefined }): ReactElement | null {
  const chain = parseCriticalChain(lhr);
  if (!chain) return null;
  return (
    <MetricSection
      title="Critical request chain"
      subtitle="Dependency chains the browser followed to render the page — the longest (critical) path is highlighted."
    >
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-mute)]">
        <span>
          Longest chain{' '}
          <span className="tabular-nums text-[var(--color-ink-soft)]">
            {formatMs(chain.longestMs)}
          </span>
        </span>
        <span className="tabular-nums">{chain.total} requests</span>
      </div>
      <ul className="overflow-hidden rounded-md border border-[var(--color-line-soft)] text-[11px]">
        {chain.rows.map((r, i) => (
          <li
            key={`${r.url}-${i}`}
            className={cn(
              'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-2 py-1',
              i % 2 === 1 && 'bg-[var(--color-paper-alt)]/30',
            )}
          >
            <span className="flex min-w-0 items-center" style={{ paddingLeft: r.depth * 16 }}>
              <span
                aria-hidden="true"
                className={cn(
                  'mr-1.5 inline-block size-1.5 shrink-0 rounded-full',
                  r.isLongest ? 'bg-[var(--color-accent-brand)]' : 'bg-[var(--color-line)]',
                )}
              />
              <span
                className={cn(
                  'truncate font-mono',
                  r.isLongest ? 'text-[var(--color-ink)]' : 'text-[var(--color-ink-soft)]',
                )}
                title={r.url}
              >
                {chainLabel(r.url)}
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-[var(--color-mute)]">
              {formatBytes(r.transferSize)}
            </span>
            <span className="w-12 shrink-0 text-right tabular-nums text-[var(--color-ink-soft)]">
              {formatMs(r.endMs)}
            </span>
          </li>
        ))}
      </ul>
      {chain.total > chain.rows.length && (
        <div className="mt-1 text-[10px] text-[var(--color-mute)]">
          + {chain.total - chain.rows.length} more requests
        </div>
      )}
    </MetricSection>
  );
}
